// Aufgeteilt aus dem früheren 2600-Zeilen-Monolithen `musicXmlDom.ts` (Import-Pfad unverändert:
// `@/composables/musicXmlDom` zeigt auf `index.ts`). Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht
// (aktuell: bass→chordInference, notes→core, diatonic→notes, structure→notes,
// lyrics→bass, clipboard→structure, fragment→structure).
import {CASCADE_ATTR_TAGS, applyMeasureAttrs, injectCascadeAttr} from './structure';
import {chordGroupOf, findMeasureInPart, findMeasureInPartByIndex, findPartForNote, getVoiceNotes, parseMusicXml, serializeMusicXml} from './core';

// ============ Griffschrift-Vorschau: Einzeltakt-Ausschnitt ============

/**
 * Schneidet EINEN Takt aus einem MusicXML-Score in ein **eigenständiges** `<score-partwise>`-
 * Dokument (ein `<part>`, ein `<measure number="1">`) – gedacht als kleine Notations-Vorschau
 * (OSMD `load()` verlangt ein vollständiges Dokument, keinen nackten `<note>`-Schnipsel).
 *
 * Der Ziel-`<part>` wird – wie der restliche per-Note-Writeback dieses Moduls – über `staffIndex`
 * aufgelöst (nicht `partId`; siehe `findPartForNote`). **Nachwirkende `<attributes>`**
 * (`divisions`/`key`/`time`/`clef`/`staves`), die frühere Takte gesetzt haben, werden in den
 * ausgeschnittenen Takt migriert (sofern er sie nicht ohnehin selbst definiert) – sonst fehlte dem
 * isolierten Takt Divisions/Schlüssel/Tonart und OSMD renderte falsch. Reine String→String-Funktion
 * (kein Cross-Document-Node-Move: der Klon wird im Quell-Dokument angereichert und als String
 * eingebettet). Gibt `null` bei Fehler / nicht auffindbarem Takt.
 */
export function extractMeasureAsStandaloneMusicXml(
    musicXml: string,
    staffIndex: number,
    measureNumber: number,
    measureIndex?: number,
): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const part = findPartForNote(doc, staffIndex);
        if (!part) return null;
        const measures = [...part.querySelectorAll('measure')];
        const target = measureIndex === undefined
            ? findMeasureInPart(part, measureNumber)
            : findMeasureInPartByIndex(part, measureIndex);
        if (!target) return null;

        // Nachwirkende `<attributes>` am START des Zieltakts sammeln (aus allen vorhergehenden Takten
        // des Parts). Reihenfolge/Tags wie beim Ganztakt-Löschen (CASCADE_ATTR_TAGS).
        const running = new Map<string, Element>();
        for (const m of measures) {
            if (m === target) break;
            applyMeasureAttrs(m, running);
        }

        // Klon des Zieltakts (Nummer auf 1), fehlende nachwirkende Attribute einspeisen. Der Klon
        // bleibt im Quell-`doc` (cloneNode erbt ownerDocument) → injectCascadeAttr arbeitet
        // konfliktfrei im selben Dokument.
        const clone = target.cloneNode(true) as Element;
        clone.setAttribute('number', '1');
        for (const tag of CASCADE_ATTR_TAGS) {
            const inForce = running.get(tag);
            if (inForce) injectCascadeAttr(doc, clone, tag, inForce);
        }

        const measureXml = new XMLSerializer().serializeToString(clone);
        return '<?xml version="1.0" encoding="UTF-8"?>'
            + '<score-partwise version="3.1">'
            + '<part-list><score-part id="P1"><part-name></part-name></score-part></part-list>'
            + `<part id="P1">${measureXml}</part>`
            + '</score-partwise>';
    } catch (e) {
        console.error('Error extracting measure as standalone MusicXML:', e);
        return null;
    }
}

/**
 * Färbt EINE Note (inkl. ihrer Akkord-Gruppe) in einem eigenständigen Einzeltakt-Dokument – für die
 * **Vorschau-Hervorhebung** der inspizierten Note (nur Anzeige, nie der echte Output/Export).
 * Adressiert die Note über `voice` + `voiceNoteIndex` im (einzigen) Takt des Fragments und setzt
 * `color` auf `<note>` UND `<notehead>` (OSMD rendert XML-Farben per Default, `ColoringMode.XML`).
 * Note nicht gefunden → Fragment unverändert; `null` nur bei Parse-/Serialize-Fehler.
 */
export function highlightNoteInStandaloneMeasure(
    fragmentXml: string,
    voice: number,
    voiceNoteIndex: number,
    color: string,
): string | null {
    try {
        const doc = parseMusicXml(fragmentXml);
        const measure = doc.querySelector('measure');
        if (!measure) return fragmentXml;
        const note = getVoiceNotes(measure, voice)[voiceNoteIndex];
        if (!note) return fragmentXml;
        for (const el of chordGroupOf(note)) {
            el.setAttribute('color', color);
            const nh = el.querySelector('notehead');
            if (nh) nh.setAttribute('color', color);
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error highlighting note in standalone measure:', e);
        return null;
    }
}
